import React, { useEffect, useState } from 'react';
import { Info } from 'react-feather';
import { FormattedMessage } from 'react-intl';
import { useForm, useFieldArray } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import ReactTooltip from 'react-tooltip';

import {
  isValidName,
  isValidGroupName,
  isValidPhoneNumberWithPrefix,
  isValidDate,
  isValidEmail,
  isValidLoyaltyNumber
} from 'utils/validators';
import { LOYALTY_NUMBER_PREFIX } from 'constants/passengers';
import PassengerType from 'components/ui/PassengerType';
import { Row, Column } from 'components/shared/Layout';
import { Cell, TableRow, Table, TableHeader } from 'components/ui/StyledTable';
import { GET_MEMBERSHIP_DETAILS } from 'store/queries';
import config from 'utils/config';

import {
  SaveButton,
  ExitButton,
  StyledTableInputWrapper,
  StyledTableInputHidden,
  StyledTableInput,
  StyledTableInputError,
  SmallText,
  ApplyToAllLabel,
  DependantTooltip,
  DeleteButton
} from '../../Passengers.styles';

const IS_ADVANCED_PASSENGER_INFORMATION_ENABLED = config('ENABLE_ADVANCED_PASSENGER_INFORMATION', { canOverride: true }) === 'true';

const formatPassengersForForm = (passengers) =>
  passengers.map(({ loyaltyNumber, ...passenger }) => ({
    loyaltyNumber:
      loyaltyNumber && `${loyaltyNumber}`.startsWith(`${LOYALTY_NUMBER_PREFIX}`)
        ? `${loyaltyNumber}`.replace(`${LOYALTY_NUMBER_PREFIX}`, '')
        : loyaltyNumber,
    ...passenger
  }));

const EditForm = ({
  passengers,
  canManageInfants,
  columnSpan,
  toggleEditMode,
  onDeleteInfant,
  onSave,
  isTableInEditMode,
  isPassengersEditable,
  isGroupBooking = false
}) => {
  const apolloClient = useApolloClient();
  const [deletingPassengerRefId, setDeletingPassengerRefId] = useState(null);

  const { register, control, handleSubmit, formState, reset, setError, clearErrors, setValue, getValues } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      passengers: formatPassengersForForm(passengers)
    }
  });

  const { errors } = formState;

  const validateName = isGroupBooking ? isValidGroupName : isValidName;
  const isSubmissionDisabled = (errors?.passengers || []).some(({ loyaltyNumber }) => loyaltyNumber);

  const hasError = ({ name, index }) => Boolean(errors && errors.passengers && errors.passengers[index] && errors.passengers[index][name]);
  const shouldShowDeleteColumn = canManageInfants && !isTableInEditMode && passengers.some(({ isInfant }) => isInfant);
  const tableColumnSpan = shouldShowDeleteColumn ? columnSpan + 1 : columnSpan;

  const { fields } = useFieldArray({
    control,
    name: 'passengers'
  });

  useEffect(() => {
    if (!isTableInEditMode) {
      reset({ passengers: formatPassengersForForm(passengers) });
    }
  }, [isTableInEditMode, passengers, reset]);

  const isPassengerRowEditable = ({ isPassengerEditable, isSuspend, isInfant }) => isPassengerEditable === true && !isSuspend && !isInfant;

  const copyInputValueToAllPassengers = (column) => {
    let firstEditablePassengerIndex = passengers.findIndex(isPassengerRowEditable);
    passengers.forEach((_, i) => {
      if (isPassengerRowEditable(passengers[i])) {
        setValue(`passengers.${i}.${column}`, getValues()?.passengers?.[firstEditablePassengerIndex][column], {
          shouldValidate: true
        });
      }
    });
  };

  const handleExit = () => {
    reset({ passengers: formatPassengersForForm(passengers) });
    toggleEditMode();
  };

  const onSubmit = async (formData) => {
    const loyaltyChecks = await Promise.all(
      formData.passengers.map(async (passenger, index) => {
        try {
          if (passenger.loyaltyNumber) {
            let hasError = false;

            const { data } = await apolloClient.query({
              query: GET_MEMBERSHIP_DETAILS,
              variables: {
                membershipNumber: `${LOYALTY_NUMBER_PREFIX}${passenger.loyaltyNumber}`
              }
            });

            const customerInfo = data.getCustomerDetails;

            if (passenger?.firstName?.toUpperCase() !== customerInfo?.firstName?.toUpperCase()) {
              clearErrors(`passengers.${index}.firstName`);
              setError(`passengers.${index}.firstName`, { type: 'validate', message: 'paxForm.validator.incorrectFirstName' });
              hasError = true;
            }

            if (passenger?.lastName?.toUpperCase() !== customerInfo?.lastName?.toUpperCase()) {
              clearErrors(`passengers.${index}.lastName`);
              setError(`passengers.${index}.lastName`, { type: 'validate', message: 'paxForm.validator.incorrectLastName' });
              hasError = true;
            }

            return !hasError;
          }

          return true;
        } catch {
          clearErrors(`passengers.${index}.loyaltyNumber`);
          setError(`passengers.${index}.loyaltyNumber`, { type: 'validate', message: 'paxForm.validator.notExistLoyaltyNumber' });

          return false;
        }
      })
    );

    if (loyaltyChecks.every(Boolean)) {
      onSave({
        ...formData,
        passengers: formData.passengers.map(({ loyaltyNumber, ...passenger }) => ({
          ...passenger,
          loyaltyNumber: loyaltyNumber ? `${LOYALTY_NUMBER_PREFIX}${loyaltyNumber}` : loyaltyNumber
        }))
      });
      toggleEditMode();
    }
  };

  const handleDeleteInfant = async (parentPassengerId) => {
    if (!parentPassengerId) {
      return;
    }

    try {
      setDeletingPassengerRefId(parentPassengerId);
      await onDeleteInfant(parentPassengerId);
    } catch {
      setDeletingPassengerRefId(null);

      return;
    }

    setDeletingPassengerRefId(null);
  };

  return (
    <form data-testid="passengers-form" onSubmit={handleSubmit(onSubmit)}>
      <Row data-cy="passengers-edit-controls" style={{ direction: 'rtl' }}>
        {isTableInEditMode && (
          <>
            <Column span={2}>
              <SaveButton type="submit" data-cy="save-passengers" data-testid="save-passengers" disabled={isSubmissionDisabled}>
                <FormattedMessage id="buttons.save" />
              </SaveButton>
            </Column>
            <Column span={2}>
              <ExitButton type="button" data-cy="exit-passengers" onClick={handleExit}>
                <FormattedMessage id="buttons.exit" />
              </ExitButton>
            </Column>
          </>
        )}
      </Row>
      <Table data-cy="passengers-table">
        <TableHeader columns={tableColumnSpan}>
          <Cell span={1}>
            <FormattedMessage id="passengerDetails.passengerId" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.firstName" />
            {isTableInEditMode ? (
              <ApplyToAllLabel id="copy-first-name-btn" onClick={() => copyInputValueToAllPassengers('firstName')}>
                <FormattedMessage id="paxForm.applyToAll" />
              </ApplyToAllLabel>
            ) : null}
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.lastName" />
            {isTableInEditMode ? (
              <ApplyToAllLabel id="copy-last-name-btn" onClick={() => copyInputValueToAllPassengers('lastName')}>
                <FormattedMessage id="paxForm.applyToAll" />
              </ApplyToAllLabel>
            ) : null}
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.paxType" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.dateOfBirth" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.contactTelephone" />
            {isTableInEditMode ? (
              <ApplyToAllLabel id="copy-contact-phone-btn" onClick={() => copyInputValueToAllPassengers('phoneNumber')}>
                <FormattedMessage id="paxForm.applyToAll" />
              </ApplyToAllLabel>
            ) : null}
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.contactEmail" />
            {isTableInEditMode ? (
              <ApplyToAllLabel id="copy-contact-email-btn" onClick={() => copyInputValueToAllPassengers('email')}>
                <FormattedMessage id="paxForm.applyToAll" />
              </ApplyToAllLabel>
            ) : null}
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="passengerDetails.loyaltyNumber" />
          </Cell>
          {IS_ADVANCED_PASSENGER_INFORMATION_ENABLED && (
            <Cell span={3}>
              <FormattedMessage id="advancedPaxInfo.passportInformation" />
            </Cell>
          )}
          {shouldShowDeleteColumn && <Cell span={1} />}
        </TableHeader>

        <div>
          {fields.map(
            (
              {
                passengerId,
                id,
                firstName,
                parentPassengerId,
                isInfant,
                lastName,
                birthDate,
                email,
                phoneNumber,
                type,
                loyaltyNumber,
                isPassengerEditable,
                disabilityType,
                isTravelDocumentRequired,
                hasTravelDocumentBeenProvided,
                isDependant,
                isSuspend
              },
              index
            ) => {
              const isFirstNameInvalid = hasError({ name: 'firstName', index });
              const isLastNameInvalid = hasError({ name: 'lastName', index });
              const isBirthDateInvalid = hasError({ name: 'birthDate', index });
              const isPhoneNumberInvalid = hasError({ name: 'phoneNumber', index });
              const isEmailInvalid = hasError({ name: 'email', index });
              const isLoyaltyNumberInvalid = hasError({ name: 'loyaltyNumber', index });
              const isFieldEditable = isPassengersEditable && isTableInEditMode && isPassengerEditable && !isSuspend && !isInfant;
              const dobPlaceholder = !isFieldEditable ? '--' : 'dd/mm/yy';
              const defaultPlaceholder = isFieldEditable ? '' : '--';
              const isLoyaltyPrefaceVisible = isFieldEditable || loyaltyNumber;
              let passportInformationLabel = 'advancedPaxInfo.informationNotRequired';

              if (isTravelDocumentRequired) {
                switch (hasTravelDocumentBeenProvided) {
                  case true:
                    passportInformationLabel = 'advancedPaxInfo.informationCompleted';
                    break;
                  case false:
                    passportInformationLabel = 'advancedPaxInfo.informationRequired';
                    break;
                  default:
                    passportInformationLabel = 'advancedPaxInfo.informationNotRequired';
                    break;
                }
              }

              return (
                <TableRow columns={tableColumnSpan} key={id} data-cy="passengers-table-row" data-testid="passengers-table-row">
                  <Cell span={1} alignItems="center">
                    {passengerId.split('_')?.[1] || passengerId}
                    <StyledTableInputHidden
                      type="text"
                      value={passengerId}
                      disabled
                      {...register(`passengers.${index}.passengerId`, { required: isFieldEditable })}
                    />
                    <StyledTableInputHidden
                      type="text"
                      value={isPassengerEditable}
                      disabled
                      {...register(`passengers.${index}.isPassengerEditable`, { required: false })}
                    />
                  </Cell>
                  <Cell span={2} alignItems="center">
                    <StyledTableInputWrapper>
                      <StyledTableInput
                        type="text"
                        defaultValue={firstName}
                        disabled={!isFieldEditable}
                        isInvalid={isFirstNameInvalid}
                        {...register(`passengers.${index}.firstName`, {
                          required: isFieldEditable,
                          validate: isFieldEditable ? validateName : true
                        })}
                      />
                      {isFirstNameInvalid && (
                        <StyledTableInputError disabled={!isFieldEditable}>
                          <FormattedMessage id={errors?.passengers?.[index]?.firstName?.message || 'passengerDetails.error.firstName'} />
                        </StyledTableInputError>
                      )}
                    </StyledTableInputWrapper>
                  </Cell>
                  <Cell span={2} alignItems="center">
                    <StyledTableInputWrapper>
                      <StyledTableInput
                        type="text"
                        defaultValue={lastName}
                        disabled={!isFieldEditable}
                        isInvalid={isLastNameInvalid}
                        {...register(`passengers.${index}.lastName`, {
                          required: isFieldEditable,
                          validate: isFieldEditable ? validateName : true
                        })}
                      />
                      {isLastNameInvalid && (
                        <StyledTableInputError disabled={!isFieldEditable}>
                          <FormattedMessage id={errors?.passengers?.[index]?.lastName?.message || 'passengerDetails.error.lastName'} />
                        </StyledTableInputError>
                      )}
                    </StyledTableInputWrapper>
                  </Cell>
                  <Cell span={2} alignItems="center">
                    <DependantTooltip>
                      <PassengerType type={type} disabilityType={disabilityType} passengerId={passengerId} />
                      {isDependant && (
                        <>
                          &nbsp;
                          <Info size={15} data-for="dependant-tooltip" data-tip />
                        </>
                      )}
                    </DependantTooltip>
                    <ReactTooltip className="tooltip" id="dependant-tooltip" data-cy="dependant-tooltip" place="bottom">
                      <FormattedMessage id="paxType.dependant" />
                    </ReactTooltip>
                  </Cell>
                  <Cell span={2} alignItems="center">
                    <StyledTableInputWrapper>
                      <StyledTableInput
                        type="text"
                        defaultValue={birthDate}
                        disabled={!isFieldEditable}
                        isInvalid={isBirthDateInvalid}
                        placeholder={dobPlaceholder}
                        {...register(`passengers.${index}.birthDate`, {
                          required: false,
                          validate: isValidDate
                        })}
                      />
                      {isBirthDateInvalid && (
                        <StyledTableInputError disabled={!isFieldEditable}>
                          <FormattedMessage id="passengerDetails.error.birthDate" />
                        </StyledTableInputError>
                      )}
                    </StyledTableInputWrapper>
                  </Cell>
                  <Cell span={2} alignItems="center">
                    <StyledTableInputWrapper>
                      <StyledTableInput
                        type="text"
                        inputMode="tel"
                        defaultValue={phoneNumber}
                        disabled={!isFieldEditable}
                        isInvalid={isPhoneNumberInvalid}
                        placeholder={defaultPlaceholder}
                        {...register(`passengers.${index}.phoneNumber`, {
                          required: isFieldEditable,
                          validate: isFieldEditable ? (value) => isValidPhoneNumberWithPrefix(value) : true
                        })}
                      />
                      {isPhoneNumberInvalid && (
                        <StyledTableInputError disabled={!isFieldEditable}>
                          <FormattedMessage id="passengerDetails.error.phoneNumber" />
                        </StyledTableInputError>
                      )}
                    </StyledTableInputWrapper>
                  </Cell>
                  <Cell span={2} alignItems="center">
                    <StyledTableInputWrapper>
                      <StyledTableInput
                        type="text"
                        inputMode="email"
                        defaultValue={email}
                        disabled={!isFieldEditable}
                        isInvalid={isEmailInvalid}
                        placeholder={defaultPlaceholder}
                        {...register(`passengers.${index}.email`, {
                          required: isFieldEditable,
                          validate: isFieldEditable ? isValidEmail : true
                        })}
                      />
                      {isEmailInvalid && (
                        <StyledTableInputError disabled={!isFieldEditable}>
                          <FormattedMessage id="passengerDetails.error.email" />
                        </StyledTableInputError>
                      )}
                    </StyledTableInputWrapper>
                  </Cell>
                  <Cell span={2} alignItems="center">
                    <StyledTableInputWrapper>
                      {isLoyaltyPrefaceVisible && <SmallText>{LOYALTY_NUMBER_PREFIX}</SmallText>}
                      <StyledTableInput
                        type="number"
                        inputMode="numeric"
                        defaultValue={loyaltyNumber}
                        disabled={!isFieldEditable}
                        isInvalid={isLoyaltyNumberInvalid}
                        placeholder={defaultPlaceholder}
                        {...register(`passengers.${index}.loyaltyNumber`, {
                          required: false,
                          validate: isValidLoyaltyNumber
                        })}
                      />
                      {isLoyaltyNumberInvalid && (
                        <StyledTableInputError disabled={!isFieldEditable}>
                          <FormattedMessage
                            id={errors?.passengers?.[index]?.loyaltyNumber?.message || 'passengerDetails.error.loyaltyNumber'}
                          />
                        </StyledTableInputError>
                      )}
                    </StyledTableInputWrapper>
                  </Cell>
                  {IS_ADVANCED_PASSENGER_INFORMATION_ENABLED && (
                    <Cell span={3} alignItems="center">
                      <StyledTableInputWrapper
                        isTravelDocumentRequired={isTravelDocumentRequired}
                        hasTravelDocumentBeenProvided={hasTravelDocumentBeenProvided}
                      >
                        <FormattedMessage id={passportInformationLabel} />
                      </StyledTableInputWrapper>
                    </Cell>
                  )}
                  {shouldShowDeleteColumn && (
                    <Cell span={1} alignItems="center">
                      {isInfant && parentPassengerId ? (
                        <DeleteButton
                          data-cy="passenger-infant-delete"
                          data-testid="passenger-infant-delete"
                          disabled={Boolean(deletingPassengerRefId)}
                          loading={deletingPassengerRefId === parentPassengerId}
                          margin="0"
                          onClick={() => handleDeleteInfant(parentPassengerId)}
                          padding="0 6px"
                          type="button"
                        >
                          <FormattedMessage id="buttons.delete" />
                        </DeleteButton>
                      ) : null}
                    </Cell>
                  )}
                </TableRow>
              );
            }
          )}
        </div>
      </Table>
    </form>
  );
};

EditForm.propTypes = {
  passengers: PropTypes.arrayOf(
    PropTypes.shape({
      passengerId: PropTypes.string.isRequired,
      parentPassengerId: PropTypes.string,
      isDependant: PropTypes.bool,
      isInfant: PropTypes.bool,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      birthDate: PropTypes.string,
      email: PropTypes.string,
      phoneNumber: PropTypes.string,
      type: PropTypes.string,
      loyaltyNumber: PropTypes.string,
      isPassengerEditable: PropTypes.bool,
      isTravelDocumentRequired: PropTypes.bool,
      hasTravelDocumentBeenProvided: PropTypes.bool
    }).isRequired
  ).isRequired,
  canManageInfants: PropTypes.bool.isRequired,
  onDeleteInfant: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isPassengersEditable: PropTypes.bool.isRequired,
  isTableInEditMode: PropTypes.bool.isRequired,
  columnSpan: PropTypes.number.isRequired,
  toggleEditMode: PropTypes.func.isRequired,
  isGroupBooking: PropTypes.bool
};

export default EditForm;
